import { useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import API, { getErrorMessage } from "../api/axios";
import { fetchPagedCollection } from "../api/paginated";
import { hasFullAccess, isAdmin, getUser } from "../auth/auth";
import { useToast } from "../components/Toast";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Grid,
  Stack,
  Avatar,
  Tooltip,
  Autocomplete,
  Collapse,
  Pagination,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PeopleIcon from "@mui/icons-material/People";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TuneIcon from "@mui/icons-material/Tune";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ACCENT, NAVY } from "../theme";
import LoadingState from "../components/ui/LoadingState";
import EmptyState from "../components/ui/EmptyState";
import SearchInput from "../components/ui/SearchInput";
import FilterBar from "../components/ui/FilterBar";
import StageStepper from "../components/ui/StageStepper";
import FilterChipBar from "../components/ui/FilterChipBar";
import RefreshButton from "../components/ui/RefreshButton";
import PageContainer from "../components/ui/PageContainer";
import DateFilter from "../components/ui/DateFilter";
import AiCvMatchDialog from "../components/AiCvMatchDialog";

// Same mapping used in Phase 3 (Dashboard) and Phase 4 (Positions): maps this
// file's existing MUI Chip `color` names to the shared STATUS_COLORS tone
// keys. "secondary" (DUPLICATE here) has no exact equivalent among the 7
// shared tones (theme.js's `secondary` palette is NAVY) so it's approximated
// as "neutral" — same documented trade-off as the other two pages, applied
// consistently rather than re-decided per file.
const MUI_COLOR_TO_TONE = {
  success: "ok",
  info: "info",
  primary: "info",
  warning: "warn",
  error: "danger",
  default: "neutral",
  secondary: "neutral",
};

// Default companies always available even if no position uses them yet.
const DEFAULT_COMPANIES = [
  "Walking Tree",
  "Profinch",
  "TCS",
  "Cerabraix",
  "Internal",
];

const emptyForm = {
  jobCode: "",
  candidateDate: "",
  recruiterName: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  linkedinUrl: "",
  panNumber: "",
  currentRole: "",
  companyName: "",
  domain: "",
  skills: "",
  relevantSkills: "",
  totalExperience: "",
  currentSalary: "",
  expectedSalary: "",
  currentLocation: "",
  expectedLocation: "",
  comments: "",
  feedback: "PENDING",
};

const STATUS_COLOR = {
  PENDING: "default",
  SHORTLISTED: "success",
  SELECTED: "info",
  L1_ROUND: "info",
  L2_ROUND: "primary",
  ON_HOLD: "warning",
  REJECTED: "error",
  DUPLICATE: "secondary",
};
const statusLabel = (k) =>
  ({
    PENDING: "Pending",
    SHORTLISTED: "Shortlisted",
    SELECTED: "Selected",
    L1_ROUND: "L1 Round",
    L2_ROUND: "L2 Round",
    ON_HOLD: "On Hold",
    REJECTED: "Rejected",
    DUPLICATE: "Duplicate",
  })[k] ||
  k ||
  "Pending";

// Derived once: feedback value -> tone key, built from the two maps above
// (STATUS_COLOR + MUI_COLOR_TO_TONE) so there is exactly one source of
// truth for "what color does each stage render as" — StageStepper needs
// this keyed directly by feedback value rather than by MUI color name.
const MUI_COLOR_TO_TONE_BY_STATUS = Object.fromEntries(
  Object.entries(STATUS_COLOR).map(([status, muiColor]) => [
    status,
    MUI_COLOR_TO_TONE[muiColor] || "neutral",
  ]),
);

// Format a date (YYYY-MM-DD or ISO) as DD/MM/YYYY with NO timezone shift.
const fmtDate = (value) => {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

// YYYY-MM-DD prefix for date-range compare (no timezone shift)
const dateOnly = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

// Status buttons that can be multi-selected (excludes "All")
const STATUS_FILTERS = [
  { key: "SHORTLISTED", label: "Shortlisted" },
  { key: "SELECTED", label: "Selected" },
  { key: "L1_ROUND", label: "L1 Round" },
  { key: "L2_ROUND", label: "L2 Round" },
  { key: "ON_HOLD", label: "On Hold" },
  { key: "REJECTED", label: "Rejected" },
];

const fields = [
  { label: "Recruiter Name", key: "recruiterName" },
  { label: "Phone", key: "phone" },
  { label: "Email", key: "email", required: true },
  { label: "LinkedIn URL", key: "linkedinUrl" },
  { label: "PAN Number", key: "panNumber" },
  { label: "Current Role", key: "currentRole" },
  { label: "Current Company", key: "companyName" },
  { label: "Domain", key: "domain" },
  { label: "Skills", key: "skills" },
  { label: "Relevant Skills", key: "relevantSkills" },
  { label: "Experience", key: "totalExperience" },
  { label: "Current Salary", key: "currentSalary" },
  { label: "Expected Salary", key: "expectedSalary" },
  { label: "Current Location", key: "currentLocation" },
  { label: "Pref. Location", key: "expectedLocation" },
];

const STATUS_OPTIONS = [
  "PENDING",
  "SHORTLISTED",
  "SELECTED",
  "L1_ROUND",
  "L2_ROUND",
  "ON_HOLD",
  "REJECTED",
  "DUPLICATE",
];

const MAX_RESUME_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXT = [".pdf", ".doc", ".docx"];

const TABLE_HEADERS = [
  "Job Code",
  "Date",
  "Recruiter",
  "Candidate", // consolidated: name + phone + email + experience + current salary
  "LinkedIn",
  "PAN",
  "Role",
  "Current Company",
  "Domain",
  "Skills",
  "Relevant Skills",
  "Exp Sal",
  "Location",
  "Pref Loc",
  "Resume",
  "Comments",
  "Status",
  "Last Updated",
  "Actions",
];

function fullName(c) {
  return (
    `${c.firstName || ""} ${c.lastName || ""}`.trim() ||
    c.candidateName ||
    "Unnamed"
  );
}

// Normalize a company name so "PROFINCH" / "Profinch" / "Profinch " all match.
const normCompany = (s) => (s || "").trim().toLowerCase();

export default function Candidates() {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [tablePage, setTablePage] = useState(1);
  const [jobs, setJobs] = useState([]);
  const [clientCompanies, setClientCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  // Multi-select status filter. Empty array = All.
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [recruiterFilter, setRF] = useState("");
  const [companyFilter, setCF] = useState("");
  const [jobCodeFilter, setJCF] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [matchCandidate, setMatchCandidate] = useState(null);

  const fullAccess = hasFullAccess();
  const canDelete = isAdmin();
  const user = getUser();
  const myName = user?.name || "";

  const todayStr = (() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  })();

  const [form, setForm] = useState({
    ...emptyForm,
    recruiterName: myName,
    candidateDate: todayStr,
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchPagedCollection("/candidates", (rows) => {
        setList(rows);
        setLoading(false);
      }),
      fetchPagedCollection("/jobs", setJobs),
    ])
      .catch((err) => console.error("[Candidates] list load failed:", err))
      .finally(() => setLoading(false));
    API.get("/clients")
      .then((r) => setClientCompanies(r.data || []))
      .catch(() => setClientCompanies([]));
  }, []);

  const fetchAll = () =>
    fetchPagedCollection("/candidates", setList).catch((err) => {
      console.error("[Candidates] refresh failed:", err);
      toast.error(getErrorMessage(err, "Could not refresh candidates"));
    });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const freshForm = () => ({
    ...emptyForm,
    recruiterName: myName,
    candidateDate: todayStr,
  });

  // Toggle a status in the multi-select; tapping "All" clears everything.
  const toggleStatus = (key) => {
    if (key === "ALL") {
      setSelectedStatuses([]);
      return;
    }
    setSelectedStatuses((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    );
  };

  // Export the currently VISIBLE (filtered) rows. If no status selected and no
  // other filters, that's everyone.
  const exportToExcel = (rowsToExport) => {
    const src = rowsToExport && rowsToExport.length ? rowsToExport : [];
    const rows = src.map((c) => ({
      "Job Code": c.jobCode,
      Date: fmtDate(c.candidateDate),
      "Recruiter Name": c.recruiterName,
      "First Name": c.firstName,
      "Last Name": c.lastName,
      Phone: c.phone,
      Email: c.email,
      "LinkedIn URL": c.linkedinUrl,
      "PAN Number": c.panNumber,
      "Current Role": c.currentRole,
      "Current Company": c.companyName,
      Domain: c.domain,
      Skills: c.skills,
      "Relevant Skills": c.relevantSkills,
      Experience: c.totalExperience || c.experienceYears || "",
      "Current Salary": c.currentSalary,
      "Expected Salary": c.expectedSalary,
      "Current Location": c.currentLocation,
      "Expected Location": c.expectedLocation,
      Feedback: c.feedback,
      Comments: c.comments,
      "Has Resume": c.hasResume ? "Yes" : "No",
      "Created At": c.createdAt,
      "Last Updated By": c.updatedBy || "",
      "Last Updated At": c.updatedAt || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Candidates");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `Candidates_${todayStr}.xlsx`,
    );
    toast.success(
      `Exported ${rows.length} candidate${rows.length !== 1 ? "s" : ""}`,
    );
  };

  const pickResume = (file) => {
    if (!file) {
      setResumeFile(null);
      return;
    }
    const name = file.name.toLowerCase();
    if (!ALLOWED_EXT.some((ext) => name.endsWith(ext))) {
      toast.error("Resume must be a PDF or Word document");
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      toast.error("Resume must be 5 MB or smaller");
      return;
    }
    setResumeFile(file);
  };

  const uploadResume = async (candidateId) => {
    const fd = new FormData();
    fd.append("file", resumeFile);
    await API.post(`/candidates/${candidateId}/resume`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const onSave = async () => {
    if (!form.firstName?.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!form.email?.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!form.jobCode?.trim()) {
      toast.error("Please select a job code");
      return;
    }
    // Hard guard against double-submit: ref updates synchronously, so a fast
    // second click is ignored even before the `saving` state re-renders.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      let candidateId = editId;
      if (editId) {
        await API.put(`/candidates/${editId}`, form);
      } else {
        const { data } = await API.post("/candidates", form);
        candidateId = data.id;
      }
      if (resumeFile && candidateId) {
        try {
          await uploadResume(candidateId);
        } catch (upErr) {
          const msg =
            upErr.response?.status === 413
              ? "Candidate saved, but the resume is larger than 5 MB"
              : upErr.response?.status === 415
                ? "Candidate saved, but the resume must be PDF or Word"
                : "Candidate saved, but the resume could not be uploaded";
          toast.error(msg + ". You can add it again via Edit.");
          setForm(freshForm());
          setResumeFile(null);
          setEditId(null);
          setShowForm(false);
          fetchAll();
          savingRef.current = false;
          setSaving(false);
          return;
        }
      }
      toast.success(editId ? "Candidate updated" : "Candidate saved");
      setForm(freshForm());
      setResumeFile(null);
      setEditId(null);
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not save candidate"));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const onEdit = (c) => {
    setForm({
      ...emptyForm,
      ...c,
      firstName: c.firstName || "",
      lastName: c.lastName || "",
      phone: c.phone || "",
      linkedinUrl: c.linkedinUrl || "",
    });
    setResumeFile(null);
    setEditId(c.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  // The 8 existing backend Candidate.Feedback enum values, in a sensible
  // display order for the stepper. No additional statuses are invented.
  const FEEDBACK_OPTIONS = [
    "PENDING",
    "SHORTLISTED",
    "L1_ROUND",
    "L2_ROUND",
    "SELECTED",
    "ON_HOLD",
    "REJECTED",
    "DUPLICATE",
  ];

  // ── Phase 8 (item H): inline stage change ───────────────────────────────────
  // CRITICAL: per the explicit instruction, the backend Candidate PUT is a
  // FULL OBJECT UPDATE. `c` here is the exact same complete candidate object
  // already loaded from GET /candidates that `onEdit` above spreads into its
  // form (see onEdit: `{ ...emptyForm, ...c }`) and that `onSave` PUTs back
  // unmodified apart from user edits — so spreading `{ ...c, feedback: next }`
  // below follows the identical, already-proven pattern. This NEVER sends a
  // partial `{ feedback: next }` payload.
  const [stageSaving, setStageSaving] = useState(null); // candidate id currently saving, or null
  const onStageChange = async (c, next) => {
    const prev = c.feedback;
    setStageSaving(c.id);
    // Optimistic UI update so the badge/stepper reflects the choice instantly;
    // rolled back below if the save fails.
    setList((cur) =>
      cur.map((row) => (row.id === c.id ? { ...row, feedback: next } : row)),
    );
    try {
      await API.put(`/candidates/${c.id}`, { ...c, feedback: next });
      toast.success(`Stage updated to ${statusLabel(next)}`);
    } catch (err) {
      // Roll back on failure — never leave the UI showing a stage that
      // wasn't actually saved.
      setList((cur) =>
        cur.map((row) => (row.id === c.id ? { ...row, feedback: prev } : row)),
      );
      toast.error(getErrorMessage(err, "Could not update stage"));
    } finally {
      setStageSaving(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this candidate?")) return;
    try {
      await API.delete(`/candidates/${id}`);
      toast.success("Candidate deleted");
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not delete candidate"));
    }
  };

  const downloadResume = async (c) => {
    try {
      const res = await API.get(`/candidates/${c.id}/resume`, {
        responseType: "blob",
      });
      saveAs(
        new Blob([res.data]),
        c.resumeFilename || `resume_${c.firstName || c.id}`,
      );
    } catch {
      toast.error("Could not download resume");
    }
  };

  const recruiterNames = [
    ...new Set(list.map((c) => c.recruiterName).filter(Boolean)),
  ].sort();

  // Dynamic, de-duplicated company list: defaults + any company in the data,
  // merged case/space-insensitively so "PROFINCH" and "Profinch" show once.
  const companyNames = useMemo(() => {
    const map = new Map(); // normalized -> display label
    DEFAULT_COMPANIES.forEach((c) => map.set(normCompany(c), c));
    // Registered client companies (so companies with no candidates yet show).
    clientCompanies.forEach((c) => {
      const raw = (c.name || "").trim();
      if (raw && !map.has(normCompany(raw))) map.set(normCompany(raw), raw);
    });
    jobs.forEach((j) => {
      const raw = (j.companyName || "").trim();
      if (!raw) return;
      const key = normCompany(raw);
      if (!map.has(key)) map.set(key, raw);
    });
    return [...map.values()].sort((a, b) => a.localeCompare(b));
  }, [jobs, clientCompanies]);

  const jobCodesForCompany = companyFilter
    ? new Set(
        jobs
          .filter(
            (j) => normCompany(j.companyName) === normCompany(companyFilter),
          )
          .map((j) => j.jobCode),
      )
    : null;

  const jobCodes = [
    ...new Set(list.map((c) => c.jobCode).filter(Boolean)),
  ].sort();

  const inDateRange = (c) => {
    if (!fromDate && !toDate) return true;
    const ds = dateOnly(c.candidateDate || c.createdAt);
    if (!ds) return false;
    if (fromDate && ds < fromDate) return false;
    if (toDate && ds > toDate) return false;
    return true;
  };

  const visible = list.filter((c) => {
    const sq = search.toLowerCase();
    const statusOk =
      selectedStatuses.length === 0 || selectedStatuses.includes(c.feedback);
    return (
      (fullName(c).toLowerCase().includes(sq) ||
        c.recruiterName?.toLowerCase().includes(sq) ||
        c.companyName?.toLowerCase().includes(sq) ||
        c.jobCode?.toLowerCase().includes(sq)) &&
      statusOk &&
      (!recruiterFilter || c.recruiterName === recruiterFilter) &&
      (!jobCodesForCompany || jobCodesForCompany.has(c.jobCode)) &&
      (!jobCodeFilter || c.jobCode === jobCodeFilter) &&
      inDateRange(c)
    );
  });
  const rowsPerPage = 50;
  const pageCount = Math.max(1, Math.ceil(visible.length / rowsPerPage));
  const visiblePage = visible.slice(
    (tablePage - 1) * rowsPerPage,
    tablePage * rowsPerPage,
  );
  useEffect(() => {
    setTablePage(1);
  }, [search, selectedStatuses, recruiterFilter, companyFilter, jobCodeFilter, fromDate, toDate]);

  const scopeName = fullAccess ? recruiterFilter : myName;
  const scopedCands = useMemo(
    () =>
      list.filter(
        (c) =>
          (!jobCodesForCompany || jobCodesForCompany.has(c.jobCode)) &&
          (!jobCodeFilter || c.jobCode === jobCodeFilter) &&
          (!scopeName || c.recruiterName === scopeName),
      ),
    [list, jobCodesForCompany, jobCodeFilter, scopeName],
  );

  const scopedPositions = useMemo(
    () =>
      jobs.filter(
        (j) =>
          (!companyFilter ||
            normCompany(j.companyName) === normCompany(companyFilter)) &&
          (!scopeName || j.spocName === scopeName),
      ).length,
    [jobs, companyFilter, scopeName],
  );

  const sc = (key) => scopedCands.filter((c) => c.feedback === key).length;
  const summaryStats = [
    { label: "Positions", value: scopedPositions },
    { label: "Profiles", value: scopedCands.length },
    { label: "Shortlisted", value: sc("SHORTLISTED") },
    { label: "Selected", value: sc("SELECTED") },
    { label: "L1", value: sc("L1_ROUND") },
    { label: "L2", value: sc("L2_ROUND") },
    { label: "Rejected", value: sc("REJECTED") },
    { label: "Pending", value: sc("PENDING") },
  ];
  const showSummary = !fullAccess || !!recruiterFilter;

  const initials = (scopeName || "??")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const LinkedInText = ({ url }) =>
    url ? (
      <Tooltip title={url}>
        <span
          style={{
            display: "block",
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 12,
            color: "#64748b",
          }}
        >
          {url}
        </span>
      </Tooltip>
    ) : (
      <Typography sx={{ fontSize: 13, color: "text.disabled" }}>—</Typography>
    );

  const hasAnyFilter =
    search ||
    selectedStatuses.length > 0 ||
    recruiterFilter ||
    companyFilter ||
    jobCodeFilter ||
    fromDate ||
    toDate;

  return (
    <PageContainer
      sx={{
        px: { xs: 2, md: 2.5 },
        py: { xs: 2, md: 2 },
        overflowX: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          pl: { xs: 5, md: 0 },
        }}
      >
        <Typography
          sx={{ color: "text.secondary", fontSize: 14, fontWeight: 600 }}
        >
          {visible.length} of {list.length} records
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <RefreshButton onClick={fetchAll} />
          <Button
            variant={showForm ? "outlined" : "contained"}
            startIcon={showForm ? <CloseIcon /> : <AddIcon />}
            onClick={() => {
              setShowForm(!showForm);
              setForm(freshForm());
              setResumeFile(null);
              setEditId(null);
            }}
          >
            {showForm ? "Close" : "Add Candidate"}
          </Button>
          {list.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => exportToExcel(visible)}
            >
              Export Excel
            </Button>
          )}
        </Stack>
      </Box>

      {/* Form */}
      {showForm && (
        <Paper sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2.5 }}>
            {editId ? "Edit Candidate" : "New Candidate"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={jobs}
                getOptionLabel={(j) =>
                  j
                    ? `${j.jobCode} — ${j.roleName || ""} @ ${j.companyName || ""}`
                    : ""
                }
                isOptionEqualToValue={(opt, val) => opt.jobCode === val.jobCode}
                value={jobs.find((j) => j.jobCode === form.jobCode) || null}
                onChange={(e, picked) =>
                  setForm({ ...form, jobCode: picked ? picked.jobCode : "" })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <span>
                        Job Code<span style={{ color: "#dc2626" }}> *</span>
                      </span>
                    }
                    placeholder="Type to search job code…"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={
                  <span>
                    First Name<span style={{ color: "#dc2626" }}> *</span>
                  </span>
                }
                name="firstName"
                value={form.firstName}
                onChange={onChange}
                placeholder="Enter first name"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={onChange}
                placeholder="Enter last name"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                type="date"
                label="Date"
                name="candidateDate"
                value={form.candidateDate || ""}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {fields.map((f) => {
              const lockRecruiter = f.key === "recruiterName" && !fullAccess;
              return (
                <Grid item xs={12} sm={6} md={4} key={f.key}>
                  <TextField
                    label={
                      f.required ? (
                        <span>
                          {f.label}
                          <span style={{ color: "#dc2626" }}> *</span>
                        </span>
                      ) : (
                        f.label
                      )
                    }
                    name={f.key}
                    value={form[f.key]}
                    onChange={onChange}
                    InputProps={{
                      readOnly: lockRecruiter,
                      endAdornment: lockRecruiter ? (
                        <InputAdornment position="end">
                          <LockIcon
                            sx={{ fontSize: 15, color: "text.disabled" }}
                          />
                        </InputAdornment>
                      ) : undefined,
                    }}
                    placeholder={`Enter ${f.label.toLowerCase()}`}
                  />
                </Grid>
              );
            })}

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label="Feedback"
                name="feedback"
                value={form.feedback}
                onChange={onChange}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {statusLabel(s)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                fullWidth
                sx={{
                  height: 56,
                  justifyContent: "flex-start",
                  textTransform: "none",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {resumeFile
                    ? resumeFile.name
                    : editId && form.resumeFilename
                      ? `Current: ${form.resumeFilename}`
                      : "Resume (PDF / Word, ≤5 MB)"}
                </Box>
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => pickResume(e.target.files?.[0])}
                />
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Comments / Reason"
                name="comments"
                value={form.comments || ""}
                onChange={onChange}
                placeholder="Write the reason / notes (e.g. why rejected, L1 feedback)…"
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
            <Button
              variant="contained"
              onClick={saving ? undefined : onSave}
              disabled={saving}
            >
              {saving
                ? "Saving…"
                : editId
                  ? "Update Candidate"
                  : "Save Candidate"}
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => {
                setShowForm(false);
                setForm(freshForm());
                setResumeFile(null);
                setEditId(null);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Search + date filter (single compact toolbar row) */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        sx={{ mb: 1.5 }}
      >
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search candidate, recruiter, company or job code…"
          sx={{ flex: 1, minWidth: 220 }}
        />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <DateFilter
            from={fromDate}
            to={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
            isToday={fromDate === todayStr && toDate === todayStr}
            isAllTime={!fromDate && !toDate}
            onToday={() => {
              setFromDate(todayStr);
              setToDate(todayStr);
            }}
            onAllTime={() => {
              setFromDate("");
              setToDate("");
            }}
          />
          <Button
            size="small"
            variant={showFilters ? "contained" : "outlined"}
            startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
            sx={
              showFilters
                ? { bgcolor: NAVY, color: "#fff", "&:hover": { bgcolor: NAVY } }
                : {}
            }
            onClick={() => setShowFilters((v) => !v)}
          >
            Filters
          </Button>
        </Stack>
      </Stack>

      {/* Filters (collapsible) */}
      <Collapse in={showFilters} unmountOnExit>
        <FilterBar
          activeCount={
            [
              recruiterFilter,
              companyFilter,
              jobCodeFilter,
              selectedStatuses.length > 0,
            ].filter(Boolean).length
          }
          onClear={() => {
            setRF("");
            setCF("");
            setJCF("");
            setSelectedStatuses([]);
          }}
        >
          <TuneIcon
            sx={{
              color: "text.secondary",
              display: { xs: "none", sm: "block" },
              fontSize: 20,
            }}
          />
          {/* ── Filter chips (was a multi-select dropdown) ──────────────────
              Design Gap Audit item H.2: status is a small, fixed set, so it
              is a good fit for the prototype's chip style. Job Code /
              Recruiter / Company below are left as dropdowns since those
              are large, dynamic picklists where chips would be worse UX,
              not better — this is a deliberate scope decision, not an
              oversight. Underlying state (selectedStatuses / toggleStatus)
              is completely unchanged from before. */}
          <FilterChipBar
            options={STATUS_FILTERS}
            selected={selectedStatuses}
            onToggle={toggleStatus}
          />
          <TextField
            select
            size="small"
            label="Job Code"
            value={jobCodeFilter}
            onChange={(e) => setJCF(e.target.value)}
            sx={{ minWidth: 140, maxWidth: 190 }}
          >
            <MenuItem value="">All Job Codes</MenuItem>
            {jobCodes.map((jc) => (
              <MenuItem key={jc} value={jc}>
                {jc}
              </MenuItem>
            ))}
          </TextField>
          {fullAccess && (
            <TextField
              select
              size="small"
              label="Recruiter"
              value={recruiterFilter}
              onChange={(e) => setRF(e.target.value)}
              sx={{ minWidth: 140, maxWidth: 190 }}
            >
              <MenuItem value="">All Recruiters</MenuItem>
              {recruiterNames.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            size="small"
            label="Company"
            value={companyFilter}
            onChange={(e) => setCF(e.target.value)}
            sx={{ minWidth: 140, maxWidth: 190 }}
          >
            <MenuItem value="">All Companies</MenuItem>
            {companyNames.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </FilterBar>
      </Collapse>

      {/* Recruiter summary strip */}
      {showSummary && !loading && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Avatar
              sx={{ bgcolor: NAVY, width: 44, height: 44, fontWeight: 700 }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>
                {scopeName || "All Recruiters"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {fullAccess
                  ? "Recruiter performance summary"
                  : "Your contribution so far"}
                {companyFilter ? ` · ${companyFilter}` : ""}
              </Typography>
            </Box>
          </Stack>
          <Grid container spacing={1.5}>
            {summaryStats.map((st) => (
              <Grid item xs={6} sm={3} md={12 / 8} key={st.label}>
                <Box
                  sx={{
                    bgcolor: "background.default",
                    borderRadius: 1.5,
                    p: 1.5,
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}
                  >
                    {st.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mt: 0.75,
                    }}
                  >
                    {st.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Loading skeleton */}
      {loading ? (
        <Paper sx={{ p: 2 }}>
          <LoadingState variant="rows" rows={5} columns={6} />
        </Paper>
      ) : visible.length === 0 ? (
        <Paper sx={{ py: 2 }}>
          <EmptyState
            icon={<PeopleIcon sx={{ fontSize: 28, color: ACCENT }} />}
            title="No candidates found"
            description={
              hasAnyFilter
                ? "Try clearing your filters or search."
                : "Add your first candidate to get started."
            }
            actionLabel="Add Candidate"
            onAction={() => {
              setShowForm(true);
              setForm(freshForm());
              setResumeFile(null);
              setEditId(null);
              window.scrollTo(0, 0);
            }}
          />
        </Paper>
      ) : (
        <>
          {/* Desktop table */}
          <Paper
            sx={{ overflow: "hidden", display: { xs: "none", md: "block" } }}
          >
            <TableContainer
              sx={{
                // Cap height so the horizontal scrollbar is always in view, and
                // force both scrollbars to always render (not OS-overlay hidden).
                maxHeight: "calc(100vh - 175px)",
                overflowX: "scroll",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  height: 12,
                  width: 12,
                  WebkitAppearance: "none",
                  display: "block",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#e2e8f0",
                  borderRadius: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#94a3b8",
                  borderRadius: 8,
                  border: "2px solid #e2e8f0",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#64748b",
                },
                scrollbarWidth: "auto",
                scrollbarColor: "#94a3b8 #e2e8f0",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1900,
                  // Clean, flat rows — horizontal separators only (matches Positions).
                  "& .MuiTableCell-root": {
                    borderBottom: "1px solid #eef1f5",
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    py: 0.6,
                    px: 1.5,
                    fontSize: 13,
                    lineHeight: 1.35,
                    verticalAlign: "middle",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 220,
                  },
                  "& tbody tr:hover": {
                    backgroundColor: "#f6f8fb",
                  },
                  // Flat, uppercase header — no fill box, subtle bottom border.
                  "& thead .MuiTableCell-root": {
                    backgroundColor: "#ffffff",
                    color: "#64748b",
                    fontWeight: 700,
                    fontSize: 11.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    borderBottom: "2px solid #e6eaf0",
                    whiteSpace: "nowrap",
                  },
                  // Keep the Job Code column (1st) compact so it doesn't hog space.
                  "& th:first-of-type, & td:first-of-type": {
                    width: 110,
                    minWidth: 110,
                    maxWidth: 130,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {TABLE_HEADERS.map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          whiteSpace: "nowrap",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePage.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {c.jobCode || "—"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {fmtDate(c.candidateDate)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {c.recruiterName}
                      </TableCell>
                      {/* ── Phase 8 (item H): consolidated identity cell ──
                          Merges what used to be 6 separate columns (First
                          Name, Last Name, Phone, Email, Exp, Curr Sal) into
                          one compact cell, matching the prototype's denser
                          identity model. No data is removed — every field
                          below still exists, just presented together, and
                          the Excel export (exportToExcel, unchanged) still
                          exports each field as its own column for anyone
                          who needs the granular spreadsheet view.
                          NOTE: the prototype's identity cell also shows
                          "notice period" — this app's Candidate data model
                          has no notice-period field, so it's honestly
                          omitted here rather than invented. */}
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                          {fullName(c)}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        >
                          {c.phone || "—"}
                        </Typography>
                        {c.email ? (
                          <Tooltip title={c.email}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "text.secondary",
                                maxWidth: 190,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {c.email}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography
                            sx={{ fontSize: 12, color: "text.secondary" }}
                          >
                            —
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: "text.secondary",
                            mt: 0.25,
                          }}
                        >
                          {c.totalExperience ||
                            (c.experienceYears
                              ? `${c.experienceYears}yr`
                              : "—")}
                          {c.currentSalary ? ` · ${c.currentSalary}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <LinkedInText url={c.linkedinUrl} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {c.panNumber || "—"}
                      </TableCell>
                      <TableCell>{c.currentRole || "—"}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {c.companyName || "—"}
                      </TableCell>
                      <TableCell>{c.domain || "—"}</TableCell>
                      <TableCell>{c.skills || "—"}</TableCell>
                      <TableCell>{c.relevantSkills || "—"}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {c.expectedSalary || "—"}
                      </TableCell>
                      <TableCell>{c.currentLocation || "—"}</TableCell>
                      <TableCell>{c.expectedLocation || "—"}</TableCell>
                      <TableCell>
                        {c.hasResume ? (
                          <Button
                            size="small"
                            startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
                            onClick={() => downloadResume(c)}
                            sx={{ textTransform: "none" }}
                          >
                            Download
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>

                      <TableCell
                        sx={{ width: 240, minWidth: 240, maxWidth: 240 }}
                      >
                        {c.comments ? (
                          <Tooltip
                            title={
                              <span style={{ whiteSpace: "pre-wrap" }}>
                                {c.comments}
                              </span>
                            }
                          >
                            <Typography
                              sx={{
                                fontSize: 13,
                                lineHeight: 1.5,
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              }}
                            >
                              {c.comments}
                            </Typography>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <StageStepper
                          value={c.feedback}
                          onChange={(next) => onStageChange(c, next)}
                          disabled={stageSaving === c.id}
                          options={FEEDBACK_OPTIONS}
                          labelMap={statusLabel}
                          toneMap={MUI_COLOR_TO_TONE_BY_STATUS}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {c.updatedAt ? (
                          <>
                            <Box sx={{ fontSize: 12, fontWeight: 600 }}>
                              {c.updatedBy || "—"}
                            </Box>
                            <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                              {new Date(c.updatedAt + "Z").toLocaleString(
                                "en-IN",
                                {
                                  timeZone: "Asia/Kolkata",
                                },
                              )}
                            </Box>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Compare CV with a position">
                            <IconButton size="small" color="primary" aria-label={`Match ${fullName(c)} to a position`} onClick={() => setMatchCandidate(c)}>
                              <AutoAwesomeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                            onClick={() => onEdit(c)}
                          >
                            Edit
                          </Button>
                          {canDelete && (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                              onClick={() => onDelete(c.id)}
                            >
                              Del
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Mobile cards */}
          <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" } }}>
            {visiblePage.map((c) => (
              <Paper key={c.id} sx={{ overflow: "hidden" }}>
                <Box
                  sx={{
                    p: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                      {fullName(c)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "text.secondary",
                      }}
                    >
                      {c.recruiterName}
                    </Typography>
                    {c.jobCode && (
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 700, mt: 0.5 }}
                      >
                        {c.jobCode}
                      </Typography>
                    )}
                    {c.candidateDate && (
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}
                      >
                        {fmtDate(c.candidateDate)}
                      </Typography>
                    )}
                  </Box>
                  <StageStepper
                    value={c.feedback}
                    onChange={(next) => onStageChange(c, next)}
                    disabled={stageSaving === c.id}
                    options={FEEDBACK_OPTIONS}
                    labelMap={statusLabel}
                    toneMap={MUI_COLOR_TO_TONE_BY_STATUS}
                  />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                    {[
                      { label: "Phone", value: c.phone },
                      { label: "Email", value: c.email },
                      { label: "Current Company", value: c.companyName },
                      { label: "Role", value: c.currentRole },
                      { label: "Domain", value: c.domain },
                      {
                        label: "Experience",
                        value:
                          c.totalExperience ||
                          (c.experienceYears ? `${c.experienceYears} yr` : "-"),
                      },
                      { label: "Curr. Sal", value: c.currentSalary },
                      { label: "Exp. Sal", value: c.expectedSalary },
                      { label: "Location", value: c.currentLocation },
                      { label: "Pref. Loc", value: c.expectedLocation },
                      { label: "Skills", value: c.skills },
                      { label: "Relevant Skills", value: c.relevantSkills },
                      { label: "PAN", value: c.panNumber },
                    ].map((item) => (
                      <Grid item xs={6} key={item.label}>
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: "text.secondary",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 0.6,
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {item.value || "—"}
                        </Typography>
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "text.secondary",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                        }}
                      >
                        LinkedIn
                      </Typography>
                      <LinkedInText url={c.linkedinUrl} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "text.secondary",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                        }}
                      >
                        Resume
                      </Typography>
                      {c.hasResume ? (
                        <Button
                          size="small"
                          startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
                          onClick={() => downloadResume(c)}
                          sx={{ textTransform: "none", px: 0 }}
                        >
                          Download
                        </Button>
                      ) : (
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          —
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                  {c.comments && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        sx={{
                          fontSize: 10,
                          color: "text.secondary",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        Comments / Reason
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          bgcolor: "background.default",
                          borderRadius: 1,
                          p: 1.25,
                          whiteSpace: "pre-wrap",
                          mt: 0.5,
                        }}
                      >
                        {c.comments}
                      </Typography>
                    </Box>
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
                      onClick={() => setMatchCandidate(c)}
                    >
                      Match CV
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                      onClick={() => onEdit(c)}
                    >
                      Edit
                    </Button>
                    {canDelete && (
                      <Button
                        fullWidth
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
                        onClick={() => onDelete(c.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Paper>
            ))}
          </Stack>
          <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <Pagination
              count={pageCount}
              page={Math.min(tablePage, pageCount)}
              onChange={(_, value) => setTablePage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        </>
      )}
      <AiCvMatchDialog
        candidate={matchCandidate}
        jobs={jobs}
        onClose={() => setMatchCandidate(null)}
      />
    </PageContainer>
  );
}
